<script lang="ts" setup>
import RichTextField from '@/components/common/RichTextField.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import MediaList from '@/components/media/MediaList.vue';
import { MediaPurpose } from '@/models/media.model';
import {
  Municipality,
  nameRules,
  postalCodeRules,
} from '@/models/municipality.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import {
  MunicipalitiesActions,
  MunicipalityStoreState,
} from '@/stores/municipalities.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';

const store = useStore<ShiftCMSState & { ms: MunicipalityStoreState }>();

const events = useEvents();

const props = defineProps({
  data: {
    type: Object as PropType<Municipality>,
  },
});

const valid = ref<boolean>(false);

const municipality = ref<Municipality>({
  id: NEW_ID,
  name: '',
  description: '',
  postalCodes: [],
  centerCoordinates: { lat: 0, lng: 0 },
  media: [],
  communities: [],
  impactRelevant: true,
});

const action = computed<string>(() =>
  municipality.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveMunicipality = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (municipality.value.id === NEW_ID) {
      success = await store.dispatch(
        MunicipalitiesActions.AddMunicipality,
        municipality.value,
      );
    } else {
      success = await store.dispatch(
        MunicipalitiesActions.UpdateMunicipality,
        municipality.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved Municipality',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(MunicipalitiesActions.GetMunicipalities);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save Municipality',
      type: 'error',
    });
  }
};

const updateLanguage = async () => {
  if (municipality.value.id === NEW_ID) {
    Object.assign(municipality, { title: '' });
  } else {
    const cat = await store.dispatch(
      MunicipalitiesActions.GetMunicipality,
      municipality.value.id,
    );

    if (cat) {
      Object.assign(municipality.value, { ...cat });
    }
  }
};

const postalCodeDialog = ref<boolean>(false);
const postalCodes = ref<string>('');

const parseValue = () => {
  const codes = postalCodes.value
    .replace(/,/g, ' ')
    .replace(/\s\s/g, ' ')
    .split(' ');
  municipality.value.postalCodes = codes;
};

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  if (props.data) {
    Object.assign(municipality.value, {
      ...props.data,
    });
  }
});

const removeLabel = async () => {
  if (
    municipality.value &&
    municipality.value.id !== NEW_ID &&
    confirm('Are you absolutely sure you want to delete this "Municipality"?')
  ) {
    store.dispatch(
      MunicipalitiesActions.DeleteMunicipality,
      municipality.value.id,
    );

    store.commit(Mutations.HideDrawer);
  }
};
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveMunicipality">
    <v-container>
      <h2>{{ $t(action) }} a municipality <CurrentLanguage /></h2>
      <v-row>
        <v-col class="mx-auto" cols="7">
          <v-text-field
            v-model="municipality.name"
            :rules="nameRules"
            label="Name"
            hint="Not used on municipality page. Use a top level header in the description"
            required
          ></v-text-field>
        </v-col>
        <v-col>
          <v-checkbox
            v-model="municipality.impactRelevant"
            label="Show on impact site"
          />
        </v-col>
      </v-row>

      <RichTextField
        label="Description"
        :height="200"
        v-model="municipality.description"
        :media="municipality.media"
      />

      <v-row>
        <v-col cols="11">
          <v-combobox
            label="Postal Codes"
            v-model="municipality.postalCodes"
            multiple
            chips
            hint="Just the numbers please"
            clearable
            :rules="postalCodeRules"
          />
        </v-col>
        <v-col>
          <v-dialog v-model="postalCodeDialog" width="600">
            <template v-slot:activator="{ props: activatorProps }">
              <v-btn
                style="position: relative; top: 8px"
                size="32"
                icon
                color="option"
                v-bind="activatorProps"
              >
                <v-icon icon="mdi-content-paste" />
              </v-btn>
            </template>
            <template v-slot:default="{ isActive }">
              <v-card>
                <v-card-text>
                  <v-text-field
                    label="Paste your list of postal codes here"
                    v-model="postalCodes"
                  />
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>

                  <v-btn
                    icon
                    @click="
                      isActive.value = false;
                      parseValue();
                    "
                    color="info"
                  >
                    <v-icon icon="mdi-close" />
                  </v-btn>
                </v-card-actions>
              </v-card>
            </template>
          </v-dialog>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-text-field
            label="Latitude"
            hint="Used to find near communities"
            v-model.number="municipality.centerCoordinates.lat"
          />
        </v-col>
        <v-col>
          <v-text-field
            label="Longitude"
            hint="Used to find near communities"
            v-model.number="municipality.centerCoordinates.lng"
          />
        </v-col>
      </v-row>

      <MediaList
        v-model="municipality"
        :required="[MediaPurpose.BackgroundImage]"
      />
      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn
            class="mt-2"
            type="button"
            color="warning"
            @click="removeLabel"
            block
            >Delete</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
pre {
  font-family: sans-serif;
  font-size: 12px;
  background-color: rgb(255, 230, 171);
  display: block;
  height: 100%;
  padding: 8px;
}
</style>
